import { Box, CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import TablePaginationActions from '../../../base-components/TablePaginationActions';
import dayjs from 'dayjs';

const BASE_URL = process.env.REACT_APP_BASE_URL;
function POSInvoices({ company_id, location_id, role }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Fetch bank accounts when component mounts
  useEffect(() => {
    if (company_id) {
      getBankAccounts();
    }
  }, [company_id]);

  useEffect(() => {
    if (company_id && location_id && !isSearching) {
      getInvoices();
    }
  }, [company_id, location_id, startDate, endDate, isSearching]);

  const getBankAccounts = async () => {
    try {
      const response = await fetch(`${BASE_URL}company-bank-accounts/company/${company_id}`);
      const data = await response.json();
      if (response.ok) {
        setBankAccounts(data.bankAccounts || []);
        console.log('Bank accounts fetched:', data.bankAccounts);
      } else {
        console.log('No bank accounts found or error:', data.message);
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const getInvoices = async () => {
    setLoading(true);
    try {
      // Fetch POS orders and extract invoice information
      let url = `${BASE_URL}pos?company_id=${company_id}&location_id=${location_id}`;
      if (startDate) {
        url += `&startDate=${startDate}`;
      }
      if (endDate) {
        url += `&endDate=${endDate}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with orders property
        const orders = Array.isArray(data) ? data : (data.orders || []);
        // Filter only completed orders with POS invoices and map to invoice format
        const invoices = orders
          .filter((order) => order.status === 'Completed' && order.invoice_id && order.invoice_id.invoice_type === 'POS')
          .map((order) => ({
            ...order.invoice_id,
            invoice_no: order.voucher_id?.invoice_no || order.order_id,
            order_id: order.order_id,
            member_id: order.member_id,
            voucher_date: order.voucher_id?.voucher_date || order.invoice_id.created_at,
            pos_order: order,
          }));
        setInvoices(invoices);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const paginatedInvoices =
    !invoices || invoices.length === 0
      ? []
      : rowsPerPage === -1
        ? invoices
        : invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewInvoice = (invoice) => {
    // Navigate to POS invoice view page using order _id
    if (invoice.pos_order?._id) {
      navigate(`/pos-invoice/${invoice.pos_order._id}`);
    } else if (invoice._id) {
      // Fallback: try using invoice _id if it's actually an order _id
      navigate(`/pos-invoice/${invoice._id}`);
    }
  };

  const handleInvoiceSearch = async () => {
    if (!invoiceSearch.trim()) {
      setIsSearching(false);
      getInvoices();
      return;
    }

    setIsSearching(true);
    setLoading(true);
    try {
      let url = `${BASE_URL}invoice/search/invoice/${encodeURIComponent(invoiceSearch.trim())}?company_id=${company_id}&location_id=${location_id}`;
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle response format: { invoices: [...] } or direct array
        let foundInvoices = [];
        if (data.invoices && Array.isArray(data.invoices)) {
          foundInvoices = data.invoices;
        } else if (Array.isArray(data)) {
          foundInvoices = data;
        }

        // Map the invoice search results to our invoice format
        const mappedInvoices = foundInvoices
          .filter((inv) => inv.invoice_type === 'POS' && inv.pos_order)
          .map((inv) => ({
            ...inv,
            invoice_no: inv.voucher?.invoice_no || inv.pos_order?.order_id || inv.invoice_no,
            order_id: inv.pos_order?.order_id || 'N/A',
            member_id: inv.pos_order?.member_id,
            voucher_date: inv.voucher?.voucher_date || inv.pos_order?.created_at || inv.created_at,
            pos_order: inv.pos_order,
          }));

        setInvoices(mappedInvoices);
      } else {
        // No invoices found
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error searching by invoice number:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setInvoiceSearch('');
    setIsSearching(false);
    getInvoices();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-xl text-gx-ink-soft">POS Invoices</h2>
        <div className="flex gap-4 items-center">
          {/* Invoice Number Search */}
          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder="Search by Invoice No..."
              value={invoiceSearch}
              onChange={(e) => setInvoiceSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleInvoiceSearch();
                }
              }}
              className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft w-64"
            />
            <button
              onClick={handleInvoiceSearch}
              className="gx-cta"
            >
              Search
            </button>
            {isSearching && (
              <button
                onClick={handleClearSearch}
                className="gx-cta-secondary"
              >
                Clear
              </button>
            )}
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setIsSearching(false);
              setInvoiceSearch('');
            }}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setIsSearching(false);
              setInvoiceSearch('');
            }}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-20">
          <CircularProgress
            thickness={4}
            size={35}
            sx={{
              'svg circle': {
                stroke: 'url(#gradientColors)',
              },
            }}
          />
        </div>
      ) : invoices && invoices.length !== 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5 animate-fadeIn overflow-y-auto overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice No</TableCell>
                <TableCell>Order ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInvoices.map((invoice, index) => (
                <TableRow hover key={index}>
                  <TableCell className="font-semibold">{invoice.invoice_no || 'N/A'}</TableCell>
                  <TableCell>{invoice.order_id || 'N/A'}</TableCell>
                  <TableCell>{dayjs(invoice.voucher_date || invoice.created_at || invoice.createdAt).format('MMM DD, YYYY')}</TableCell>
                  <TableCell>
                    {invoice.member_id?.name ||
                      invoice.pos_order?.member_id?.name ||
                      invoice.pos_order?.customer_name ||
                      'Walk-in Customer'}
                  </TableCell>
                  <TableCell className="font-semibold">Rs. {invoice.total_amount?.toFixed(2) || 0}</TableCell>
                  <TableCell>Rs. {invoice.discount?.toFixed(2) || 0}</TableCell>
                  <TableCell>
                    <div
                      className={`font-bold p-2 px-4 rounded-full text-center ${invoice.payment_status === 'Paid'
                          ? 'bg-green-700 text-white'
                          : invoice.payment_status === 'Pending'
                            ? 'bg-yellow-700 text-white'
                            : invoice.payment_status === 'Partial'
                              ? 'bg-blue-800 text-white'
                              : 'bg-red-700 text-white'
                        }`}
                    >
                      {invoice.payment_status || 'Paid'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      className="text-gx-ink font-bold hover:cursor-pointer hover:text-green-700"
                      onClick={() => handleViewInvoice(invoice)}
                    >
                      <FontAwesomeIcon icon={faEye} /> View
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={8}
                  count={invoices.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  slotProps={{
                    select: {
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    },
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-10 rounded-lg m-10">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">No POS Invoices Found</h3>
        </div>
      )}
    </>
  );
}

export default POSInvoices;

